import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { PasswordInput } from '@/components/ui/password-input';
import { changePasswordResetMutation } from '@/mutations/auth/changePasswordResetMutation';
import { useNavigate } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

const getFormSchema = (t: TFunction) =>
  z
    .object({
      password: z
        .string({
          required_error: t(
            'auth.changePassword.form.fields.password.error.required'
          ),
        })
        .min(6, t('auth.changePassword.form.fields.password.error.min')),
      confirmPassword: z
        .string({
          required_error: t(
            'auth.changePassword.form.fields.confirmPassword.error.required'
          ),
        })
        .min(6, t('auth.changePassword.form.fields.confirmPassword.error.min')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('auth.changePassword.form.fields.confirmPassword.error.match'),
      path: ['confirmPassword'],
    });

type ChangePasswordFormProps = {
  token: number;
};

export function ChangePasswordForm({ token }: ChangePasswordFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formSchema = getFormSchema(t);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const { mutate: changePassword, isPending } = useMutation({
    ...changePasswordResetMutation(token),
    onSuccess: (response) => {
      if (response.isOk()) {
        toast.success(t('auth.changePassword.toast.success'), {
          action: {
            label: t('auth.changePassword.toast.success.login'),
            onClick: () => {
              navigate({ to: '/login' });
            },
          },
        });
      } else {
        if (response.error.type === 'TOKEN_EXPIRED') {
          toast.error(t('auth.changePassword.toast.error.tokenExpired'));
        }

        if (response.error.type === 'PASSWORD_TOO_WEAK') {
          form.setError('password', {
            message: t('auth.changePassword.form.fields.password.error.min'),
          });
        }

        if (response.error.type === 'INVALID_TOKEN') {
          toast.error(t('auth.changePassword.toast.error.invalidToken'));
        }

        if (response.error.type === 'API_ERROR') {
          toast.error(t('auth.changePassword.toast.error.generic'));
        }
      }
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    changePassword(values.password);
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
        data-testid="change-password-form"
      >
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('auth.changePassword.form.fields.password.label')}
              </FormLabel>
              <FormControl>
                <PasswordInput
                  placeholder={t(
                    'auth.changePassword.form.fields.password.placeholder'
                  )}
                  {...field}
                  data-testid="change-password-reset-password-input"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('auth.changePassword.form.fields.confirmPassword.label')}
              </FormLabel>
              <FormControl>
                <PasswordInput
                  placeholder={t(
                    'auth.changePassword.form.fields.confirmPassword.placeholder'
                  )}
                  {...field}
                  data-testid="change-password-confirm-password-input"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="w-full"
          disabled={isPending}
          data-testid="change-password-submit-button"
          isLoading={isPending}
        >
          {t('auth.changePassword.changePassword')}
        </Button>
      </form>
    </Form>
  );
}
